import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useSearchHistoryStore } from '@features/search/model/useSearchStore'
import { useAddressStore } from '@features/shipping-methods'

import { useCategoriesModal } from '@widgets/modal-categories/hooks/useCategoriesModal'
import { ProductPreviewCard } from '@widgets/product-preview'
import { ProductPreviewCardProps } from '@widgets/product-preview/ui'


import { SearchProductsQuery, usePopularProductsQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import ButtonClose from '@shared/ui/button-close'
import CardSkeleton from '@shared/ui/card-skeleton'
import Wrapper from '@shared/ui/wrapper'

import EmptyPopover from './empty-popover/empty-popover'

import styles from './styles.module.scss'


export interface SearchResultsPopoverProps {
  searchData: SearchProductsQuery;
  searchLoading: boolean;
  inModal?: boolean;
  IS_EMPTY: boolean;
  HAS_RESULTS: boolean;
  onClose: () => void;
  onSearch: () => void;
  onHistoryClick: (val: string) => void;
}

export function SearchResultsPopover ({
  searchData,
  searchLoading,
  inModal,
  IS_EMPTY,
  HAS_RESULTS,
  onClose,
  onSearch,
  onHistoryClick,
}: SearchResultsPopoverProps) {
  const { history, clearHistory } = useSearchHistoryStore()
  const { shopId } = useAddressStore()
  const { data: popularProducts } = usePopularProductsQuery({
    variables: {
      shopId: shopId,
      pager: {
        limit: 4,
        page: 1,
      },
    },
  })
  const { t: ts } = useTranslation('search')

  const { openModal: openCategoriesModal } = useCategoriesModal()

  const handleCatalogClick = useCallback(() => {
    openCategoriesModal()
    onClose()
  }, [ openCategoriesModal, onClose ])

  return (
    <Wrapper
      className={cn(styles.wrapper, {
        [styles.empty]: IS_EMPTY,
        [styles.inModal]: inModal,
      })}
    >
      <div className={styles['search-top']}>
        {!!history.length && !IS_EMPTY && (
          <div className={styles.history}>
            <h3 className={styles.title}>{ts('searchHistory')}</h3>
            <div className={styles.history__grid}>
              {history.map((h) => {
                return (
                  <button
                    className={cn('reset-button', styles.history__item)}
                    onClick={() => onHistoryClick(h)}
                    key={h}
                  >
                    {h}
                  </button>
                )
              })}
            </div>
            <button
              className={cn('reset-button', styles['history__clear'])}
              type="button"
              onClick={clearHistory}
            >
              {ts('clearHistory')}
            </button>
          </div>
        )}

        {!inModal && (
          <ButtonClose
            className={styles.close}
            theme="grey"
            onClick={onClose}
          />
        )}
      </div>

      {IS_EMPTY && <EmptyPopover onGoToCatalog={handleCatalogClick} />}

      {popularProducts?.popularProducts?.edges &&
        popularProducts.popularProducts.edges.length > 0 &&
        !searchData &&
        !searchLoading &&
        !IS_EMPTY && (
        <div className={styles.popular}>
          <h3 className={styles.title}>{ts('recommended')}</h3>
          <div className={styles.products}>
            {popularProducts.popularProducts.edges.map((product) => (
              <div onClick={onClose} key={product?.id}>
                <ProductPreviewCard
                  data={product as ProductPreviewCardProps['data']}
                  key={product?.id}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {HAS_RESULTS && (
        <div className={styles.products}>
          {searchData?.searchProducts?.edges?.filter(p => !!p).map((product) => (
            <div onClick={onClose} key={product?.id}>
              <ProductPreviewCard
                data={product as ProductPreviewCardProps['data']}
                key={product?.id}
              />
            </div>
          ))}
        </div>
      )}

      {!HAS_RESULTS && searchLoading && (
        <div className={styles.products}>
          {Array.from({ length: 4 }).map((_, index) => (
            <CardSkeleton key={index} />
          ))}
        </div>
      )}

      {HAS_RESULTS && (
        <Button
          className={styles.root__results}
          theme="grey"
          onClick={() => onSearch()}
        >
          {ts('allResults')}
        </Button>
      )}
    </Wrapper>
  )
}
